import {useApplyFormStore} from "@/stores/applyForm";
import {useLoadingStore} from "@/stores/loading";
import {useEffect} from "react";
import {useNavigate} from "react-router-dom";
import * as Common from "@/utils/common";
import * as Config from "@/config";
import {postApply} from "@/api";
import {useMessageModalStore} from "@/stores/message";

export default function ApplyConfirm() {
  const navigate = useNavigate();
  const setLoading = useLoadingStore((state) => state.setLoading);
  const setMessage = useMessageModalStore((state) => state.setMessage);

  const {applyForm, attributes} = useApplyFormStore();
  useEffect(() => {
    if (!applyForm || !attributes) {
      navigate("/apply");
    } else {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [applyForm, attributes]);

  const onClickSend = async () => {
    if (applyForm == null) {
      return;
    }
    const dataAttributeValues = await Common.inputAttributeToPostParam(
      attributes,
      applyForm
    );
    const projectCategoryId = await Common.getProjectCategoryId(dataAttributeValues, attributes);
    const files = await Common.filesToPostParam(applyForm.files);
    const postData = {
      project_category_id: projectCategoryId,
      data_attribute_values: dataAttributeValues,
      attached_file: files,
    };
    setLoading(true);
    postApply(postData)
      .then((res) => {
        if (res.data.result) {
          navigate("/apply_complete");
        } else {
          setMessage(Config.MESSAGE_NO_E37);
        }
      })
      .catch((err) => {
        setMessage(Config.MESSAGE_NO_E37);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onClickCancel = () => {
    navigate(-1);
  };

  // onBeforeunloadイベントのハンドラー関数
  const handleBeforeUnload = (e: any) => {
    // ページ遷移やリロードのキャンセル
    e.preventDefault();
    // カスタムメッセージの設定（ブラウザによっては無視される）
    e.returnValue =
      "入力内容が未送信です。入力内容が消えますがよろしいでしょうか？";
  };

  // コンポーネントがマウントされた時にイベントを登録
  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    // コンポーネントがアンマウントされた時にイベントを解除
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const getValue = (columnName: string) => {
    const id = attributes.find(
      (item: any) => item.column_name === columnName
    )?.id;
    if (!id) {
      return "";
    }
    const composed = applyForm?.attributes?.[id];
    if (!composed) {
      return "";
    }
    return Common.extractValue(composed);
  };

  return (
    <div
      className="uk-flex uk-flex-column uk-flex-middle"
      data-uk-height-viewport="expand: true"
    >
      <div className="sub-header">
        <p className="uk-text-large uk-text-bold uk-margin-remove">
          各種お問い合わせ 入力内容確認画面
        </p>
      </div>
      <div className="uk-flex uk-width-3-4 uk-margin-top">
        <div className="step-content">
          <ul className="step">
            <li>メールアドレスの入力</li>
            <li>メールアドレスの送信完了</li>
            <li>お客様情報の登録</li>
            <li className="is-current">登録内容の確認</li>
            <li>お申し込みの完了</li>
          </ul>
        </div>
      </div>
      <div className="uk-width-3-4 uk-margin-top">
        <div className="uk-text-medium">▼問合せ者情報</div>
        <table className="confirm-tbl">
          <tr>
            <td>問合せ区分</td>
            <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_INQUIRER_TYPE)}</td>
          </tr>
          <tr>
            <td>団体区分</td>
            <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_GROUP_TYPE)}</td>
          </tr>
          <tr>
            <td>法人・団体名</td>
            <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_GROUP_NAME)}</td>
          </tr>
          {getValue(Config.ATTRIBUTE_COLUMN_NAME_DEPARTMENT) && (
            <tr>
              <td>部署名・所属</td>
              <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_DEPARTMENT)}</td>
            </tr>
          )}
          {getValue(Config.ATTRIBUTE_COLUMN_NAME_SECTION) && (
            <tr>
              <td>課名</td>
              <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_SECTION)}</td>
            </tr>
          )}
          {getValue(Config.ATTRIBUTE_COLUMN_NAME_UNIT) && (
            <tr>
              <td>室名または係名</td>
              <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_UNIT)}</td>
            </tr>
          )}
          {getValue(Config.ATTRIBUTE_COLUMN_NAME_POSITION) && (
            <tr>
              <td>役職</td>
              <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_POSITION)}</td>
            </tr>
          )}
          <tr>
            <td>氏名</td>
            <td>
              {getValue(Config.ATTRIBUTE_COLUMN_NAME_LAST_NAME)}{" "}
              {getValue(Config.ATTRIBUTE_COLUMN_NAME_FIRST_NAME)}
            </td>
          </tr>
          <tr>
            <td>ふりがな</td>
            <td>
              {getValue(Config.ATTRIBUTE_COLUMN_NAME_LAST_NAME_KANA)}{" "}
              {getValue(Config.ATTRIBUTE_COLUMN_NAME_FIRST_NAME_KANA)}
            </td>
          </tr>
          <tr>
            <td>電話</td>
            <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_PHONE_NUMBER)}</td>
          </tr>
          <tr>
            <td>住所</td>
            <td>
              <p>〒{getValue(Config.ATTRIBUTE_COLUMN_NAME_ZIP_CODE)}</p>
              <p>
                {getValue(Config.ATTRIBUTE_COLUMN_NAME_PREF)}
                {getValue(Config.ATTRIBUTE_COLUMN_NAME_CITY)}
                {getValue(Config.ATTRIBUTE_COLUMN_NAME_HOUSE_NUMBER)}{" "}
                {getValue(Config.ATTRIBUTE_COLUMN_NAME_BUILDING_NAME)}
              </p>
            </td>
          </tr>
          <tr>
            <td>メールアドレス</td>
            <td>{getValue(Config.ATTRIBUTE_COLUMN_NAME_EMAIL_ADDRESS)}</td>
          </tr>
        </table>
      </div>
      <div className="uk-width-3-4 uk-margin-top">
        <div className="uk-text-medium">▼問合せ内容</div>
        <table className="confirm-tbl">
          <tr>
            <td>問合せ区分</td>
            <td className="line-break">
              {getValue(Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_TYPE)}
            </td>
          </tr>
          <tr>
            <td>問合せタイトル</td>
            <td className="line-break">
              {getValue(Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_TITLE)}
            </td>
          </tr>
          <tr>
            <td>問合せ内容</td>
            <td className="line-break">
              {getValue(Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_DETAIL)}
            </td>
          </tr>
          {(applyForm?.files ?? []).length > 0 && (
            <tr>
              <td>ファイル添付</td>
              <td>
                {applyForm?.files?.map((item, index) => {
                  return <p key={index}>{item.file.name}</p>;
                })}
              </td>
            </tr>
          )}
          <tr>
            <td>個人情報の取扱い</td>
            <td>同意する</td>
          </tr>
        </table>
      </div>
      <div className="uk-flex uk-flex-center uk-margin-top uk-margin-bottom">
        <button
          className="uk-button--m uk-button-cancel"
          onClick={onClickCancel}
        >
          戻る
        </button>
        <button
          className="uk-button--m uk-button-refer uk-margin-left"
          onClick={onClickSend}
        >
          送信
        </button>
        <div style={{height: 80}}/>
      </div>
    </div>
  );
}
